// Dialogs and toasts (T-110) on Radix primitives: focus is trapped and restored, Escape
// closes, and toasts are announced to screen readers.

import * as Dialog from "@radix-ui/react-dialog";
import * as Toast from "@radix-ui/react-toast";
import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

import { t } from "../i18n";
import { Icon } from "./icons";
import { Button } from "./ui";

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel,
  destructive = false,
  busy = false,
  disabled = false,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  busy?: boolean;
  /// Not ready to confirm yet (unlike busy, the label stays).
  disabled?: boolean;
  onConfirm: () => void;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(440px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 rounded-[12px] border border-rule bg-ground p-5 shadow-lg focus:outline-none">
          <div className="flex items-start justify-between gap-3">
            <Dialog.Title className="m-0 text-lg font-semibold">{title}</Dialog.Title>
            <Dialog.Close asChild>
              <button type="button" className="rounded-[6px] border-0 bg-transparent p-1 text-steel hover:text-ink" aria-label={t("common.close")}>
                <Icon name="close" />
              </button>
            </Dialog.Close>
          </div>
          <Dialog.Description asChild>
            <div className="mt-2 text-sm text-steel">{body}</div>
          </Dialog.Description>
          <div className="mt-5 flex justify-end gap-2">
            <Dialog.Close asChild>
              <Button>{t("common.cancel")}</Button>
            </Dialog.Close>
            <Button kind={destructive ? "danger" : "primary"} disabled={busy || disabled} onClick={onConfirm}>
              {busy ? t("common.working") : confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

type ToastKind = "info" | "success" | "error";
interface ToastItem {
  id: number;
  message: string;
  kind: ToastKind;
}

const ToastContext = createContext<(message: string, kind?: ToastKind) => void>(() => {});

/// `const toast = useToast(); toast(t("common.saved"), "success")`
export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const show = useCallback((message: string, kind: ToastKind = "info") => {
    setItems((current) => [...current.slice(-2), { id: Date.now() + Math.random(), message, kind }]);
  }, []);
  return (
    <ToastContext.Provider value={show}>
      <Toast.Provider swipeDirection="right" duration={4000} label={t("common.notifications")}>
        {children}
        {items.map((item) => (
          <Toast.Root
            key={item.id}
            type={item.kind === "error" ? "foreground" : "background"}
            onOpenChange={(open) => {
              if (!open) setItems((current) => current.filter((i) => i.id !== item.id));
            }}
            className="flex items-center gap-2.5 rounded-[10px] border border-rule bg-ground px-3.5 py-2.5 text-sm shadow-lg"
          >
            <Icon name={item.kind === "error" ? "alert" : "check"} className={item.kind === "error" ? "text-err" : item.kind === "success" ? "text-ok" : "text-steel"} />
            <Toast.Description className="flex-1">{item.message}</Toast.Description>
            <Toast.Close className="rounded-[6px] border-0 bg-transparent p-0.5 text-steel hover:text-ink" aria-label={t("common.close")}>
              <Icon name="close" size={16} />
            </Toast.Close>
          </Toast.Root>
        ))}
        <Toast.Viewport className="fixed bottom-4 right-4 z-50 m-0 flex w-[min(380px,calc(100vw-32px))] list-none flex-col gap-2 p-0 outline-none" />
      </Toast.Provider>
    </ToastContext.Provider>
  );
}
